/* ===========================================================
   app.jsx — picks the screen

   signed out → Sign in · testing gate → Proto's "not open yet" ·
   first visit → Welcome · otherwise the route. Ctrl/⌘ K opens the
   search menu anywhere. Going offline or back online gets a Proto
   line, and the list/outbox catch up when the network returns.
   =========================================================== */

import { NotesPage, DrivePage } from './pages/NotesDrive.jsx';
import { useEffect, useState } from 'preact/hooks';
import { session, online, store, mode } from './state.js';
import { route, go } from './lib/router.js';
import { Toaster, Modals, toast, Spinner } from './ui/ui.jsx';
import { ProtoCard, backOnlineLine, offlineLine } from './ui/proto.jsx';
import { hostApp } from './lib/hosts.js';
import { loadLocalList, refreshList, flushOutbox, prefetch } from './sync/docs.js';
import { SignIn } from './pages/SignIn.jsx';
import { Gate } from './pages/Gate.jsx';
import { Welcome } from './pages/Welcome.jsx';
import { Home } from './pages/Home.jsx';
import { DocPage } from './pages/DocPage.jsx';
import { Settings } from './pages/Settings.jsx';
import { Trash } from './pages/Trash.jsx';
import { Lost } from './pages/Lost.jsx';
import { FilesPage, LocalDocPage } from './pages/Files.jsx';
import { Palette } from './panels/Palette.jsx';
import { ContextMenuHost } from './ui/context.jsx';

export function App() {
  const s = session.value;
  const r = route.value;
  const [palette, setPalette] = useState(false);

  /* the doc list: this device's copy at once, then the server's */
  useEffect(() => {
    if (s.status !== 'ready' || !store.value) return;
    loadLocalList().then(() => refreshList()).then(() => prefetch()).catch(err => console.warn('[list]', err));
  }, [s.status, store.value]);

  useEffect(() => {
    const on = () => {
      online.value = true;
      toast(backOnlineLine(), { proto: true });
      if (session.value.status === 'ready') flushOutbox().then(() => refreshList()).catch(() => {});
    };
    const off = () => {
      online.value = false;
      if (!r.parts[0] || r.parts[0] !== 'd') toast(offlineLine(), { proto: true });
    };
    const key = e => {
      if ((e.ctrlKey || e.metaKey) && e.key.toLowerCase() === 'k') { e.preventDefault(); setPalette(p => !p); }
    };
    const open = () => setPalette(true);
    window.addEventListener('online', on);
    window.addEventListener('offline', off);
    window.addEventListener('keydown', key);
    window.addEventListener('dp:palette', open);
    return () => {
      window.removeEventListener('online', on);
      window.removeEventListener('offline', off);
      window.removeEventListener('keydown', key);
      window.removeEventListener('dp:palette', open);
    };
  }, [r.parts[0]]);

  useEffect(() => { document.documentElement.dataset.mode = mode.value; }, [mode.value]);

  return (
    <>
      <a class="skip" href="#main">Skip to content</a>
      <Screen s={s} r={r} />
      {palette && <Palette onClose={() => setPalette(false)} />}
      <Modals />
      <Toaster />
      <ContextMenuHost />
    </>
  );
}

function Screen({ s, r }) {
  const [top, a] = r.parts;

  // a shared link works for everyone (signed out = read only)
  if (top === 's' && a) return <SharedLink token={a} />;
  if (top === 'd' && a && r.query.link && s.status !== 'ready') {
    if (s.status === 'loading') return <Loading />;
    return <DocPage id={a} link={r.query.link} />;
  }

  switch (s.status) {
    case 'loading': return <Loading />;
    case 'signed-out': return <SignIn />;
    case 'offline-first':
      return <div class="center-page"><ProtoCard title="No internet yet" text="I need the internet once to sign you in on this device. After that, Docs Protocol works offline too." actions={<button class="btn primary" type="button" onClick={() => location.reload()}>Try again</button>} /></div>;
    case 'error':
      return <div class="center-page"><ProtoCard title="Couldn't start" error={s.error} actions={<button class="btn primary" type="button" onClick={() => location.reload()}>Try again</button>} /></div>;
    case 'gate': return <Gate />;
    case 'welcome': return <Welcome />;
    default: break;
  }

  // notes.weatherprotocol.org / drive.weatherprotocol.org open on their own page
  const host = hostApp();
  if (!top && host === 'notes') return <NotesPage />;
  if (!top && host === 'drive') return <DrivePage />;
  if (!top) return <Home />;
  if (top === 'notes') return <NotesPage />;
  if (top === 'drive') return <DrivePage />;
  if (top === 'd' && a) return <DocPage key={a} id={a} link={r.query.link} />;
  if (top === 'welcome') return <Welcome replay />;
  if (top === 'settings') return <Settings />;
  if (top === 'trash') return <Trash />;
  if (top === 'files') return <FilesPage />;
  if (top === 'local' && a) return <LocalDocPage key={a} fileKey={a} />;
  if (top === 'shared') return <Home filter="shared" />;
  if (top === 'starred') return <Home filter="starred" />;
  return <Lost />;
}

function Loading() {
  return <div class="center-page"><Spinner label="Opening Docs Protocol" /></div>;
}

/** /s/<token>: open what the link points to (joining it if signed in). */
function SharedLink({ token }) {
  const s = session.value;
  if (s.status === 'loading') return <Loading />;
  return <ShareResolver token={token} signedIn={s.status === 'ready'} />;
}

function ShareResolver({ token, signedIn }) {
  const [err, setErr] = useState(null);
  useEffect(() => {
    import('./lib/api.js').then(({ api }) => api(`/api/share/${encodeURIComponent(token)}`, { method: 'POST' }))
      .then(r => {
        if (signedIn) refreshList().catch(() => {});
        go(`/d/${r.doc.id}${signedIn && r.doc.role !== 'view' ? '' : `?link=${encodeURIComponent(token)}`}`, { replace: true });
      })
      .catch(setErr);
  }, [token]);
  if (err) {
    return (
      <div class="center-page">
        <ProtoCard title="This link doesn't open anything" error={err}
          actions={<button class="btn primary" type="button" onClick={() => go('/')}>Go to Docs Protocol</button>} />
      </div>
    );
  }
  return <Loading />;
}
